import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import PageLoader from '../../components/common/PageLoader';
import api from '../../services/api';
import Swal from 'sweetalert2';
import './Tickets.css';

function TicketView() {
    const { id }     = useParams();
    const navigate   = useNavigate();
    const bottomRef  = useRef(null);

    const [loading, setLoading]     = useState(true);
    const [ticket, setTicket]       = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const [replyForm, setReplyForm] = useState({
        status: '',
        message: ''
    });
    const [replyErrors, setReplyErrors] = useState({});

    const fetchTicket = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get(`/tickets/${id}`);
            if (response.data.success) {
                const d = response.data.data;
                setTicket(d);
                setReplyForm(prev => ({ ...prev, status: d.status }));
            }
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load ticket' });
            navigate('/tickets');
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    useEffect(() => {
        fetchTicket();
    }, [fetchTicket]);

    // Scroll to bottom of thread on load
    useEffect(() => {
        if (ticket) {
            setTimeout(() => {
                bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        }
    }, [ticket]);

    const validateReply = () => {
        const newErrors = {};
        if (!replyForm.status)        newErrors.status  = 'Status is required';
        if (!replyForm.message.trim()) newErrors.message = 'Reply message is required';
        setReplyErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleReplySubmit = async (e) => {
        e.preventDefault();
        if (!validateReply()) return;

        setSubmitting(true);
        try {
            const response = await api.post(`/tickets/${id}/reply`, replyForm);
            if (response.data.success) {
                // Optimistically append reply
                setTicket(prev => ({
                    ...prev,
                    status:  replyForm.status,
                    replies: [...prev.replies, response.data.data]
                }));
                setReplyForm(prev => ({ ...prev, message: '' }));
                setReplyErrors({});
            }
        } catch (error) {
            const serverErrors = error.response?.data?.errors || {};
            if (Object.keys(serverErrors).length > 0) {
                const mapped = {};
                Object.keys(serverErrors).forEach(k => { mapped[k] = serverErrors[k][0]; });
                setReplyErrors(mapped);
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: error.response?.data?.message || 'Failed to add reply'
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    const getStatusClass = (status) => {
        switch (status) {
            case 'open':    return 'status-open';
            case 'pending': return 'status-pending';
            case 'closed':  return 'status-closed';
            default:        return 'status-open';
        }
    };

    if (loading) {
    return <MainLayout><PageLoader force copy="Loading ticket..." /></MainLayout>;
  }

    if (!ticket) return null;

    return (
        <MainLayout>
            <div className="ticket-view-container">

                {/* Back Button + Header */}
                <div className="tv-page-header">
                    <button className="btn-back" onClick={() => navigate('/tickets')}>
                        <i className="fas fa-arrow-left"></i>
                        Back to Tickets
                    </button>
                    <span className={`status-badge ${getStatusClass(ticket.status)}`}>
                        {ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
                    </span>
                </div>

                <div className="tv-grid">

                    {/* ── Left: Thread ── */}
                    <div className="tv-thread-card">
                        {/* Ticket Info */}
                        <div className="tv-ticket-info">
                            <div className="tv-ticket-meta">
                                <span className="tv-ticket-id">Ticket #{ticket.id}</span>
                                <span className="tv-ticket-date">{ticket.created_at}</span>
                            </div>
                            <h4 className="tv-ticket-subject">{ticket.subject}</h4>
                            <div className="tv-raised-by">
                                <i className="fas fa-user-circle"></i>
                                Raised by <strong>{ticket.raised_by}</strong>
                            </div>
                        </div>

                        {/* Original Message */}
                        <div className="tv-message-thread">
                            <div className="tv-message original">
                                <div className="tv-message-avatar original-avatar">
                                    {ticket.raised_by.charAt(0)}
                                </div>
                                <div className="tv-message-content">
                                    <div className="tv-message-header">
                                        <span className="tv-message-author">{ticket.raised_by}</span>
                                        <span className="tv-message-time">{ticket.created_at}</span>
                                        <span className="tv-original-tag">Original</span>
                                    </div>
                                    <p className="tv-message-text">{ticket.message}</p>
                                    {ticket.attachment && (
                                        <a
                                            href={ticket.attachment}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="tv-attachment-link"
                                        >
                                            <i className="fas fa-paperclip"></i>
                                            View Attachment
                                        </a>
                                    )}
                                </div>
                            </div>

                            {/* Replies */}
                            {ticket.replies.map((reply, idx) => {
                                const isAuthUser = reply.user_id === ticket.auth_id;
                                return (
                                    <div
                                        key={reply.id}
                                        className={`tv-message ${isAuthUser ? 'mine' : 'other'}`}
                                    >
                                        <div className={`tv-message-avatar ${isAuthUser ? 'mine-avatar' : 'other-avatar'}`}>
                                            {reply.user_name.charAt(0)}
                                        </div>
                                        <div className="tv-message-content">
                                            <div className="tv-message-header">
                                                <span className="tv-message-author">{reply.user_name}</span>
                                                <span className="tv-message-time">{reply.created_at}</span>
                                            </div>
                                            <p className="tv-message-text">{reply.message}</p>
                                        </div>
                                    </div>
                                );
                            })}
                            <div ref={bottomRef}></div>
                        </div>
                    </div>

                    {/* ── Right: Reply Form ── */}
                    <div className="tv-reply-card">
                        <div className="tv-reply-header">
                            <i className="fas fa-reply"></i>
                            <h5>Reply & Update Status</h5>
                        </div>

                        <form onSubmit={handleReplySubmit} className="tv-reply-form">

                            {/* Status */}
                            <div className="tv-form-group">
                                <label>
                                    Status <span className="required">*</span>
                                </label>
                                <div className="tv-status-options">
                                    {['open', 'pending', 'closed'].map(s => (
                                        <button
                                            key={s}
                                            type="button"
                                            className={`tv-status-btn ${replyForm.status === s ? `active-${s}` : ''}`}
                                            onClick={() => {
                                                setReplyForm(prev => ({ ...prev, status: s }));
                                                if (replyErrors.status)
                                                    setReplyErrors(prev => ({ ...prev, status: '' }));
                                            }}
                                        >
                                            <i className={`fas ${
                                                s === 'open'    ? 'fa-lock-open' :
                                                s === 'pending' ? 'fa-clock'     : 'fa-lock'
                                            }`}></i>
                                            {s.charAt(0).toUpperCase() + s.slice(1)}
                                        </button>
                                    ))}
                                </div>
                                {replyErrors.status && (
                                    <span className="tm-error">
                                        <i className="fas fa-exclamation-circle"></i>
                                        {replyErrors.status}
                                    </span>
                                )}
                            </div>

                            {/* Message */}
                            <div className="tv-form-group">
                                <label>
                                    Reply Message <span className="required">*</span>
                                </label>
                                <textarea
                                    value={replyForm.message}
                                    onChange={(e) => {
                                        setReplyForm(prev => ({ ...prev, message: e.target.value }));
                                        if (replyErrors.message)
                                            setReplyErrors(prev => ({ ...prev, message: '' }));
                                    }}
                                    placeholder="Type your reply here..."
                                    rows="5"
                                    className={replyErrors.message ? 'error' : ''}
                                ></textarea>
                                {replyErrors.message && (
                                    <span className="tm-error">
                                        <i className="fas fa-exclamation-circle"></i>
                                        {replyErrors.message}
                                    </span>
                                )}
                            </div>

                            <button
                                type="submit"
                                className="btn-reply-submit"
                                disabled={submitting || ticket.status === 'closed'}
                            >
                                {submitting ? (
                                    <>
                                        <span className="btn-spinner"></span>
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-paper-plane"></i>
                                        Reply & Update
                                    </>
                                )}
                            </button>

                            {ticket.status === 'closed' && (
                                <div className="tv-closed-notice">
                                    <i className="fas fa-lock"></i>
                                    This ticket is closed. Change status to reply.
                                </div>
                            )}
                        </form>

                        {/* Ticket Details Panel */}
                        <div className="tv-details-panel">
                            <div className="tv-details-title">Ticket Details</div>
                            <div className="tv-detail-row">
                                <span className="tv-detail-label">Ticket ID</span>
                                <span className="tv-detail-value">#{ticket.id}</span>
                            </div>
                            <div className="tv-detail-row">
                                <span className="tv-detail-label">Status</span>
                                <span className={`status-badge sm ${getStatusClass(ticket.status)}`}>
                                    {ticket.status}
                                </span>
                            </div>
                            <div className="tv-detail-row">
                                <span className="tv-detail-label">Raised By</span>
                                <span className="tv-detail-value">{ticket.raised_by}</span>
                            </div>
                            <div className="tv-detail-row">
                                <span className="tv-detail-label">Created</span>
                                <span className="tv-detail-value">{ticket.created_at}</span>
                            </div>
                            <div className="tv-detail-row">
                                <span className="tv-detail-label">Replies</span>
                                <span className="tv-detail-value">{ticket.replies.length}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
}

export default TicketView;
    
